import React, { useState, useEffect } from 'react';
import { api } from '../../api.js';
import AddressAutocomplete from '../../components/AddressAutocomplete.jsx';
import RelatedLoadsTab from './RelatedLoadsTab.jsx';
import PhoneInput from '../../components/PhoneInput.jsx';
import { displayPhone } from '../../phone.js';

const blank = {
  name: '', legalName: '', dateToStartWork: '', carrierType: '',
  contactName: '', contactEmail: '', phone: '', extension: '',
  address: '', city: '', province: '', postalCode: '', country: 'Canada',
  tollFree: '', fax: '',
  requireCreditReport: false, requireReferenceCheck: false, requireFactoringApproval: false,
  status: 'Active', remarks: '',
  billingContact: '', billingEmail: '', billingPhone: '', billingExt: '',
  billingAddress: '', billingCity: '', billingProvince: '', billingPostalCode: '', billingCountry: 'Canada',
  billingFax: '', currency: 'CDN', billingTerms: '', creditLimit: '', billingNotes: '',
  discountType: '', discountMode: '', discountValue: '', broker: '', factoringCompany: '',
  salesmanId: '', commissionPay: '', payValue: '', scaleOnSite: false, appointment: false,
  mcNumber: '', dotNumber: '', authorityType: '', authorityStatus: '', insuranceCompany: '',
  insurancePolicyNumber: '', cargoCoverage: '', liabilityCoverage: '', insuranceExpiry: '', safetyRating: ''
};

function fromCarrier(c) {
  if (!c) return blank;
  return {
    name: c.name || '', legalName: c.legal_name || '', dateToStartWork: c.date_to_start_work || '',
    carrierType: c.carrier_type || '', contactName: c.contact_name || '', contactEmail: c.contact_email || '',
    phone: c.phone || '', extension: c.extension || '',
    address: c.address || '', city: c.city || '', province: c.province || '',
    postalCode: c.postal_code || '', country: c.country || 'Canada',
    tollFree: c.toll_free || '', fax: c.fax || '',
    requireCreditReport: !!c.require_credit_report, requireReferenceCheck: !!c.require_reference_check,
    requireFactoringApproval: !!c.require_factoring_approval,
    status: c.status || 'Active', remarks: c.remarks || '',
    billingContact: c.billing_contact || '', billingEmail: c.billing_email || '', billingPhone: c.billing_phone || '',
    billingExt: c.billing_ext || '', billingAddress: c.billing_address || '', billingCity: c.billing_city || '',
    billingProvince: c.billing_province || '', billingPostalCode: c.billing_postal_code || '',
    billingCountry: c.billing_country || 'Canada', billingFax: c.billing_fax || '',
    currency: c.currency || 'CDN', billingTerms: c.billing_terms || '', creditLimit: c.credit_limit ?? '',
    billingNotes: c.billing_notes || '',
    discountType: c.discount_type || '', discountMode: c.discount_mode || '', discountValue: c.discount_value ?? '',
    broker: c.broker || '', factoringCompany: c.factoring_company || '',
    salesmanId: c.salesman_id || '', commissionPay: c.commission_pay || '', payValue: c.pay_value || '',
    scaleOnSite: !!c.scale_on_site, appointment: !!c.appointment,
    mcNumber: c.mc_number || '', dotNumber: c.dot_number || '', authorityType: c.authority_type || '',
    authorityStatus: c.authority_status || '', insuranceCompany: c.insurance_company || '',
    insurancePolicyNumber: c.insurance_policy_number || '', cargoCoverage: c.cargo_coverage ?? '',
    liabilityCoverage: c.liability_coverage ?? '', insuranceExpiry: c.insurance_expiry || '',
    safetyRating: c.safety_rating || ''
  };
}

const TABS = ['Primary Info', 'Carrier Information', 'Authorities', 'Manage Contacts', 'Related Loads'];

function ContactFormModal({ carrierId, carrierName, contact, onClose, onSaved }) {
  const [type, setType] = useState(contact?.type || '');
  const [name, setName] = useState(contact?.name || '');
  const [phone, setPhoneVal] = useState(contact?.phone || '');
  const [extension, setExtension] = useState(contact?.extension || '');
  const [email, setEmail] = useState(contact?.email || '');
  const [sendPickupConfirmation, setSendPickup] = useState(!!contact?.send_pickup_confirmation);
  const [sendDeliveryConfirmation, setSendDelivery] = useState(!!contact?.send_delivery_confirmation);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);


  const reset = () => {
    setType(contact?.type || ''); setName(contact?.name || ''); setPhoneVal(contact?.phone || '');
    setExtension(contact?.extension || ''); setEmail(contact?.email || '');
    setSendPickup(!!contact?.send_pickup_confirmation); setSendDelivery(!!contact?.send_delivery_confirmation);
  };

  const save = async () => {
    if (!type) { setError('Type is required.'); return; }
    if (!name.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError(null);
    const body = { type, name, phone, extension, email, sendPickupConfirmation, sendDeliveryConfirmation };
    try {
      if (contact) {
        await api(`/dispatch/carriers/${carrierId}/contacts/${contact.id}`, { method: 'PATCH', body });
      } else {
        await api(`/dispatch/carriers/${carrierId}/contacts`, { method: 'POST', body });
      }
      onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => { e.stopPropagation(); onClose(); }}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>Add Contacts For: {carrierName}</h3>
          <button type="button" className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr 1fr 0.7fr', gap: 14, marginBottom: 14 }}>
            <label>Type <span className="req">*</span><br/>
              <select value={type} onChange={e => setType(e.target.value)}>
                <option value="">Select</option>
                <option>Billing</option>
                <option>Dispatch</option>
                <option>Operations</option>
                <option>Accounts Payable</option>
                <option>General</option>
              </select>
            </label>
            <label>Name <span className="req">*</span><br/>
              <input value={name} onChange={e => setName(e.target.value)} />
            </label>
            <label>Phone<br/>
              <PhoneInput value={phone} onChange={setPhoneVal} />
            </label>
            <label>Extn #<br/>
              <input value={extension} onChange={e => setExtension(e.target.value)} />
            </label>
          </div>
          <label style={{ display: 'block', marginBottom: 16 }}>Email ID<br/>
            <input value={email} onChange={e => setEmail(e.target.value)} />
          </label>
          <div style={{ display: 'flex', gap: 24, marginBottom: 4 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500 }}>
              <input type="checkbox" checked={sendPickupConfirmation} onChange={e => setSendPickup(e.target.checked)} style={{ width: 'auto' }} />
              Send Pickup Confirmation Email
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500 }}>
              <input type="checkbox" checked={sendDeliveryConfirmation} onChange={e => setSendDelivery(e.target.checked)} style={{ width: 'auto' }} />
              Send Delivery Confirmation Email
            </label>
          </div>
          {error && <p style={{ color: '#c4364b', fontSize: 13, margin: '12px 0 0' }}>{error}</p>}
        </div>
        <div className="modal-actions" style={{ padding: '16px 24px', margin: 0, borderTop: '1px solid #e1e4e9', justifyContent: 'center' }}>
          <button type="button" className="primary-btn" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
          <button type="button" onClick={reset}>Reset</button>
        </div>
      </div>
    </div>
  );
}

export default function CarrierFormModal({ carrier, allCarriers = [], onClose, onSaved }) {
  const [savedId, setSavedId] = useState(carrier?.id || null);
  const [form, setForm] = useState(() => fromCarrier(carrier));
  const [tab, setTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [nameOpen, setNameOpen] = useState(false);
  const [manualAddress, setManualAddress] = useState(false);
  const [manualBillingAddress, setManualBillingAddress] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [salesmen, setSalesmen] = useState([]);

  useEffect(() => {
    api('/dispatch/salesmen').then(setSalesmen).catch(() => setSalesmen([]));
  }, []);

  const quickAddSalesman = async () => {
    const val = window.prompt('New salesman name');
    if (!val || !val.trim()) return;
    try {
      const saved = await api('/dispatch/salesmen', { method: 'POST', body: { name: val } });
      setSalesmen(list => [...list, saved]);
      set('salesmanId', saved.id);
    } catch (e) { alert(e.message); }
  };

  const [contacts, setContacts] = useState(null);
  const [contactQuery, setContactQuery] = useState('');
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  const loadContacts = () => {
    if (!savedId) return;
    setContacts(null);
    api(`/dispatch/carriers/${savedId}/contacts`).then(setContacts).catch(() => setContacts([]));
  };

  useEffect(() => { if (tab === 3 && savedId) loadContacts(); }, [tab, savedId]);

  const filteredContacts = (contacts || []).filter(c => {
    const q = contactQuery.trim().toLowerCase();
    if (!q) return true;
    return [c.name, c.type, c.email, c.phone].filter(Boolean).some(v => v.toLowerCase().includes(q));
  });

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));


  const nameMatches = form.name.trim()
    ? allCarriers.filter(c => c.name.toLowerCase().includes(form.name.trim().toLowerCase())).slice(0, 6)
    : [];

  const pickExisting = (c) => {
    setSavedId(c.id);
    setForm(fromCarrier(c));
    setNameOpen(false);
  };

  const handleLogoPick = (e) => {
    const file = e.target.files?.[0];
    if (file) setLogoPreview(URL.createObjectURL(file));
  };

  const persist = async () => {
    if (!form.name.trim()) { setError('Name is required.'); return null; }
    setSaving(true);
    setError(null);
    try {
      const saved = savedId
        ? await api(`/dispatch/carriers/${savedId}`, { method: 'PATCH', body: form })
        : await api('/dispatch/carriers', { method: 'POST', body: form });
      setSavedId(saved.id);
      return saved;
    } catch (e) {
      setError(e.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const saveAndClose = async () => { if (await persist()) onSaved(); };
  const saveAndNext = async () => {
    if (!(await persist())) return;
    setTab(t => Math.min(t + 1, TABS.length - 1));
  };
  const saveAndNew = async () => {
    if (!(await persist())) return;
    setSavedId(null);
    setForm(blank);
    setLogoPreview(null);
    setTab(0);
  };
  const reset = () => setForm(carrier ? fromCarrier(carrier) : blank);

  const creditLimitNum = form.creditLimit !== '' && form.creditLimit != null ? Number(form.creditLimit) : null;
  const hasLimit = creditLimitNum != null && creditLimitNum > 0;
  const usedNum = 0;
  const leftNum = hasLimit ? creditLimitNum - usedNum : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal customer-modal" onClick={e => e.stopPropagation()}>
        <div className="customer-modal-title">{carrier ? 'Edit carrier' : 'New carrier'}</div>

        <div className="customer-modal-tabs">
          <div className="form-tabs" style={{ marginBottom: 0 }}>
            {TABS.map((t, i) => (
              <button key={t} type="button" className={i === tab ? 'form-tab active' : 'form-tab'} onClick={() => setTab(i)}>
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="customer-modal-body">
          {tab === 0 && (
            <>
              <label>Logo</label>
              <input id="carrier-logo-input" type="file" accept="image/*" onChange={handleLogoPick} style={{ display: 'none' }} />
              <label htmlFor="carrier-logo-input" className="logo-upload" style={{ marginBottom: 18 }}>
                {logoPreview ? <img src={logoPreview} alt="Logo preview" /> : 'Click to upload an image'}
              </label>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
                <label style={{ position: 'relative' }}>Carrier Name / DBA<br/>
                  <input value={form.name}
                    onChange={e => { set('name', e.target.value); setNameOpen(true); }}
                    onFocus={() => setNameOpen(true)}
                    onBlur={() => setTimeout(() => setNameOpen(false), 150)} />
                  {nameOpen && nameMatches.length > 0 && (
                    <div className="combo-panel" style={{ top: '100%' }}>
                      <div className="combo-list">
                        {nameMatches.map(c => (
                          <div key={c.id} className="combo-option" onMouseDown={() => pickExisting(c)}>
                            {c.name}{c.city ? `, ${c.city}` : ''}{c.province ? `, ${c.province}` : ''}, {c.country || 'Canada'}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </label>
                <label>Legal Name<br/>
                  <input value={form.legalName} onChange={e => set('legalName', e.target.value)} />
                </label>
                <label>Date To Start Work<br/>
                  <input type="date" value={form.dateToStartWork} onChange={e => set('dateToStartWork', e.target.value)} />
                </label>

                <label>Carrier Type<br/>
                  <select value={form.carrierType} onChange={e => set('carrierType', e.target.value)}>
                    <option value="">—</option>
                    <option>Owner Operator</option>
                    <option>Fleet Carrier</option>
                    <option>Brokerage</option>
                    <option>Intermodal</option>
                  </select>
                </label>
                <label>Contact<br/>
                  <input value={form.contactName} onChange={e => set('contactName', e.target.value)} />
                </label>
                <label>Email<br/>
                  <input value={form.contactEmail} onChange={e => set('contactEmail', e.target.value)} />
                </label>
              </div>

              <label>Address<br/>
                <AddressAutocomplete value={form.address} onChange={val => set('address', val)}
                  onPlaceSelected={({ address, city, province, postalCode, country }) => {
                    setForm(f => ({ ...f, address, city, province, postalCode, country }));
                    setManualAddress(true);
                  }} />
              </label>
              <button type="button" className="link-toggle" style={{ marginBottom: manualAddress ? 12 : 18 }}
                onClick={() => setManualAddress(m => !m)}>
                {manualAddress ? 'Hide manual fields' : "Can't find it? Enter manually"}
              </button>
              {manualAddress && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                  <label>City<br/>
                    <input value={form.city} onChange={e => set('city', e.target.value)} />
                  </label>
                  <label>Province / State<br/>
                    <input value={form.province} onChange={e => set('province', e.target.value)} />
                  </label>
                  <label>Postal / Zip<br/>
                    <input value={form.postalCode} onChange={e => set('postalCode', e.target.value)} />
                  </label>
                  <label>Country<br/>
                    <select value={form.country} onChange={e => set('country', e.target.value)}>
                      <option>Canada</option>
                      <option>United States</option>
                    </select>
                  </label>
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Phone<br/>
                  <PhoneInput value={form.phone} onChange={v => set('phone', v)} />
                </label>
                <label>Ext<br/>
                  <input value={form.extension} onChange={e => set('extension', e.target.value)} />
                </label>
                <label>Toll Free<br/>
                  <PhoneInput value={form.tollFree} onChange={v => set('tollFree', v)} />
                </label>
                <label>Fax<br/>
                  <PhoneInput value={form.fax} onChange={v => set('fax', v)} />
                </label>
              </div>

              <label>Require Docs to Set</label>
              <div style={{ display: 'flex', gap: 20, marginBottom: 18, marginTop: 8 }}>
                {[
                  ['requireCreditReport', 'Credit Report'],
                  ['requireReferenceCheck', 'Reference Check'],
                  ['requireFactoringApproval', 'Factoring Approval']
                ].map(([key, label]) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500 }}>
                    <input type="checkbox" checked={form[key]} onChange={e => set(key, e.target.checked)} style={{ width: 'auto' }} />
                    {label}
                  </label>
                ))}
              </div>

              <label>Status</label>
              <div style={{ display: 'flex', gap: 20, marginBottom: 18, marginTop: 8 }}>
                {['Active', 'Archive'].map(s => (
                  <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500 }}>
                    <input type="radio" name="carrier-status" checked={form.status === s}
                      onChange={() => set('status', s)} style={{ width: 'auto' }} />
                    {s}
                  </label>
                ))}
              </div>

              <label>Remarks<br/>
                <textarea rows={3} value={form.remarks} onChange={e => set('remarks', e.target.value)} />
              </label>
            </>
          )}

          {tab === 1 && (
            <>
              <div className="modal-section-header">2 · Carrier Information <span>▾</span></div>

              <div className="form-box" style={{ marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
                  <label>Billing Contact<br/>
                    <input value={form.billingContact} onChange={e => set('billingContact', e.target.value)} />
                  </label>
                  <label>Billing Email<br/>
                    <input value={form.billingEmail} onChange={e => set('billingEmail', e.target.value)} />
                  </label>
                  <label>Billing Phone<br/>
                    <PhoneInput value={form.billingPhone} onChange={v => set('billingPhone', v)} />
                  </label>
                  <label>Billing Ext<br/>
                    <input value={form.billingExt} onChange={e => set('billingExt', e.target.value)} />
                  </label>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14, marginBottom: manualBillingAddress ? 12 : 0 }}>
                  <label>Billing Address<br/>
                    <AddressAutocomplete value={form.billingAddress} onChange={val => set('billingAddress', val)}
                      onPlaceSelected={({ address, city, province, postalCode, country }) => {
                        setForm(f => ({
                          ...f, billingAddress: address, billingCity: city, billingProvince: province,
                          billingPostalCode: postalCode, billingCountry: country
                        }));
                        setManualBillingAddress(true);
                      }} />
                  </label>
                  <label>Billing Fax<br/>
                    <PhoneInput value={form.billingFax} onChange={v => set('billingFax', v)} />
                  </label>
                </div>
                <button type="button" className="link-toggle" style={{ marginBottom: manualBillingAddress ? 12 : 0 }}
                  onClick={() => setManualBillingAddress(m => !m)}>
                  {manualBillingAddress ? 'Hide manual fields' : "Can't find it? Enter manually"}
                </button>
                {manualBillingAddress && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 14 }}>
                    <label>City<br/>
                      <input value={form.billingCity} onChange={e => set('billingCity', e.target.value)} />
                    </label>
                    <label>Province / State<br/>
                      <input value={form.billingProvince} onChange={e => set('billingProvince', e.target.value)} />
                    </label>
                    <label>Postal / Zip<br/>
                      <input value={form.billingPostalCode} onChange={e => set('billingPostalCode', e.target.value)} />
                    </label>
                    <label>Country<br/>
                      <select value={form.billingCountry} onChange={e => set('billingCountry', e.target.value)}>
                        <option>Canada</option>
                        <option>United States</option>
                      </select>
                    </label>
                  </div>
                )}
              </div>

              <div className="form-box" style={{ marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                  <label>Credit Limit<br/>
                    <input type="number" value={form.creditLimit} onChange={e => set('creditLimit', e.target.value)} placeholder="0.00" />
                  </label>
                  <label>Currency<br/>
                    <select value={form.currency} onChange={e => set('currency', e.target.value)}>
                      <option value="CDN">CDN</option>
                      <option value="USD">USD</option>
                    </select>
                  </label>
                  <label>Billing Terms<br/>
                    <select value={form.billingTerms} onChange={e => set('billingTerms', e.target.value)}>
                      <option value="">—</option>
                      <option>Net 15</option>
                      <option>Net 30</option>
                      <option>Net 45</option>
                      <option>Net 60</option>
                      <option>COD</option>
                      <option>Prepaid</option>
                    </select>
                  </label>
                </div>

                <div className="credit-summary">
                  <div className="credit-summary-header">
                    <strong>This carrier</strong>
                    <span className="credit-summary-badge">{hasLimit ? 'Limit set' : 'No limit set'}</span>
                  </div>
                  <div className="credit-summary-grid">
                    <div className="credit-summary-stat">
                      <div className="credit-summary-label">Credit Limit</div>
                      <div className="credit-summary-value">{hasLimit ? `$${creditLimitNum.toFixed(2)}` : '—'}</div>
                    </div>
                    <div className="credit-summary-stat">
                      <div className="credit-summary-label">Used</div>
                      <div className="credit-summary-value">${usedNum.toFixed(0)}</div>
                    </div>
                    <div className="credit-summary-stat">
                      <div className="credit-summary-label">Left</div>
                      <div className="credit-summary-value">{hasLimit ? `$${leftNum.toFixed(2)}` : '—'}</div>
                    </div>
                  </div>
                  <p className="credit-summary-note">
                    {hasLimit
                      ? 'Credit checks are on for this carrier.'
                      : 'No credit limit set — credit checks are off for this carrier.'}
                  </p>
                </div>

                <label>Billing Notes<br/>
                  <textarea rows={3} value={form.billingNotes} onChange={e => set('billingNotes', e.target.value)} />
                </label>
              </div>

              <div className="form-box">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 18 }}>
                  <label>Discount Type<br/>
                    <select value={form.discountType} onChange={e => set('discountType', e.target.value)}>
                      <option value="">Select</option>
                      <option>Volume Discount</option>
                      <option>Loyalty Discount</option>
                    </select>
                  </label>
                  <label>Discount Mode<br/>
                    <select value={form.discountMode} onChange={e => set('discountMode', e.target.value)} disabled={!form.discountType}>
                      <option value="">Select</option>
                      <option>Percentage</option>
                      <option>Flat Amount</option>
                    </select>
                  </label>
                  <label>Discount Value<br/>
                    <input type="number" value={form.discountValue} onChange={e => set('discountValue', e.target.value)} placeholder="0.00" />
                  </label>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                  <label>Broker<br/>
                    <input value={form.broker} onChange={e => set('broker', e.target.value)} />
                  </label>
                  <label>Factoring company<br/>
                    <input value={form.factoringCompany} onChange={e => set('factoringCompany', e.target.value)} />
                  </label>
                </div>

                <label style={{ display: 'block', marginBottom: 18 }}>Salesman<br/>
                  <div className="input-addon">
                    <select value={form.salesmanId} onChange={e => set('salesmanId', e.target.value)}>
                      <option value="">Select</option>
                      {salesmen.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    <button type="button" className="btn-plus" onClick={quickAddSalesman}>+</button>
                  </div>
                </label>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                  <label>Commision Pay<br/>
                    <select value={form.commissionPay} onChange={e => set('commissionPay', e.target.value)} disabled={!form.salesmanId}>
                      <option value="">Select</option>
                      <option>Percentage</option>
                      <option>Flat Amount</option>
                    </select>
                  </label>
                  <label>Pay Value<br/>
                    <input value={form.payValue} onChange={e => set('payValue', e.target.value)} disabled={!form.salesmanId} />
                  </label>
                </div>

                <div style={{ display: 'flex', gap: 24 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500 }}>
                    <input type="checkbox" checked={form.scaleOnSite} onChange={e => set('scaleOnSite', e.target.checked)} style={{ width: 'auto' }} />
                    Scale On Site
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500 }}>
                    <input type="checkbox" checked={form.appointment} onChange={e => set('appointment', e.target.checked)} style={{ width: 'auto' }} />
                    Appointment
                  </label>
                </div>
              </div>
            </>
          )}

          {tab === 2 && (
            <div className="form-box">
              <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 16 }}>
                First-pass field set — let me know if you want different fields here.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>MC Number<br/>
                  <input value={form.mcNumber} onChange={e => set('mcNumber', e.target.value)} />
                </label>
                <label>DOT Number<br/>
                  <input value={form.dotNumber} onChange={e => set('dotNumber', e.target.value)} />
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Authority Type<br/>
                  <select value={form.authorityType} onChange={e => set('authorityType', e.target.value)}>
                    <option value="">Select</option>
                    <option>Common</option>
                    <option>Contract</option>
                    <option>Broker</option>
                  </select>
                </label>
                <label>Authority Status<br/>
                  <select value={form.authorityStatus} onChange={e => set('authorityStatus', e.target.value)}>
                    <option value="">Select</option>
                    <option>Active</option>
                    <option>Pending</option>
                    <option>Revoked</option>
                  </select>
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Insurance Company<br/>
                  <input value={form.insuranceCompany} onChange={e => set('insuranceCompany', e.target.value)} />
                </label>
                <label>Insurance Policy #<br/>
                  <input value={form.insurancePolicyNumber} onChange={e => set('insurancePolicyNumber', e.target.value)} />
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 18 }}>
                <label>Cargo Coverage ($)<br/>
                  <input type="number" value={form.cargoCoverage} onChange={e => set('cargoCoverage', e.target.value)} />
                </label>
                <label>Liability Coverage ($)<br/>
                  <input type="number" value={form.liabilityCoverage} onChange={e => set('liabilityCoverage', e.target.value)} />
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <label>Insurance Expiry<br/>
                  <input type="date" value={form.insuranceExpiry} onChange={e => set('insuranceExpiry', e.target.value)} />
                </label>
                <label>Safety Rating<br/>
                  <select value={form.safetyRating} onChange={e => set('safetyRating', e.target.value)}>
                    <option value="">Select</option>
                    <option>Satisfactory</option>
                    <option>Conditional</option>
                    <option>Unsatisfactory</option>
                    <option>Not Rated</option>
                  </select>
                </label>
              </div>
            </div>
          )}

          {tab === 3 && (
            !savedId ? (
              <div className="empty">Save the carrier first (try "Save & Next" from Primary Info) to add contacts.</div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                  <button type="button" className="primary-btn" onClick={() => { setEditingContact(null); setContactModalOpen(true); }}>
                    Add Contact
                  </button>
                </div>

                <div className="panel filter-row-panel" style={{ marginBottom: 14 }}>
                  <div className="filter-row" style={{ justifyContent: 'space-between', width: '100%' }}>
                    <input placeholder="Type to filter..." value={contactQuery}
                      onChange={e => setContactQuery(e.target.value)} style={{ width: 220 }} />
                    <label style={{ fontSize: 13, color: 'var(--text-dim)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      Show:
                      <select defaultValue={10} style={{ width: 70 }}>
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                      </select>
                    </label>
                  </div>
                </div>

                {contacts === null && <div className="empty">Loading...</div>}
                {contacts && (
                  <>
                    <table>
                      <thead>
                        <tr><th>Ref#</th><th>Name</th><th>Type</th><th>Email ID</th><th>Phone</th></tr>
                      </thead>
                      <tbody>
                        {filteredContacts.length === 0 && (
                          <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-dim)' }}>No Data Found</td></tr>
                        )}
                        {filteredContacts.map(c => (
                          <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => { setEditingContact(c); setContactModalOpen(true); }}>
                            <td>{c.ref_number || '—'}</td>
                            <td>{c.name}</td>
                            <td>{c.type || '—'}</td>
                            <td>{c.email || '—'}</td>
                            <td>{displayPhone(c.phone) || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ marginTop: 10, fontSize: 13, color: 'var(--text-dim)' }}>
                      Showing {filteredContacts.length === 0 ? 0 : 1} to {filteredContacts.length} of {filteredContacts.length} entries
                    </div>
                  </>
                )}
              </>
            )
          )}

          {tab === 4 && (
            !savedId
              ? <div className="empty">Save the carrier first to see related loads.</div>
              : <RelatedLoadsTab endpoint={`/dispatch/carriers/${savedId}/loads`} />
          )}

          {error && <p style={{ color: '#c4364b', fontSize: 13, margin: '16px 0 0' }}>{error}</p>}
        </div>

        <div className="customer-modal-footer">
          <div className="modal-actions" style={{ margin: 0 }}>
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="button" onClick={reset}>Reset</button>
            <button type="button" className="primary-btn" onClick={saveAndNew} disabled={saving}>Save &amp; New</button>
            <button type="button" className="primary-btn" onClick={saveAndNext} disabled={saving || tab === TABS.length - 1}>Save &amp; Next</button>
            <button type="button" className="primary-btn" onClick={saveAndClose} disabled={saving}>
              {saving ? 'Saving...' : 'Save & Close'}
            </button>
          </div>
        </div>
      </div>

      {contactModalOpen && savedId && (
        <ContactFormModal
          carrierId={savedId}
          carrierName={form.name}
          contact={editingContact}
          onClose={() => setContactModalOpen(false)}
          onSaved={() => { setContactModalOpen(false); loadContacts(); }}
        />
      )}
    </div>
  );
}
